import { useEffect, useId, useRef } from 'react'
import { cn } from '@/lib/cn'

/** A slide-over used for detail views (jobs, users) and, from the left, for a phone's filters
 *  (WP-15). Closes on overlay click or Esc.
 *  Full width on phones (padding steps down to 16 px); content never widens the panel —
 *  wide tables inside scroll on their own.
 *  The title is `title-2` Onest 600 on one line (WP-06): a long name is cut with «…» and shown in
 *  full in its tooltip; `subtitle` (caption ink-3) carries the full name or the context.
 *  `footer` sticks to the bottom of the panel: the drawer's one primary action («Показать 1 204»,
 *  «Ответить»), destructive ones next to it — never a second primary in the body (WP-25).
 *
 *  Focus (WP-25): opening moves focus to the heading (`tabIndex=-1`, so a screen reader reads the
 *  title first and Tab starts inside the panel) without the cyan focus box — the heading is not
 *  a control, the box read as «something is selected». A control inside that already took focus
 *  (an `autoFocus` field) keeps it. Closing returns focus to whatever opened the drawer (the
 *  row's chevron, «Фильтры»), if it is still on the page. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  side = 'right',
  footer,
  id,
  children,
}: {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  side?: 'right' | 'left'
  footer?: React.ReactNode
  id?: string
  children: React.ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const headingId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (!panel.current?.contains(document.activeElement)) heading.current?.focus({ preventScroll: true })
    return () => {
      if (opener && opener !== document.body && opener.isConnected) opener.focus({ preventScroll: true })
    }
  }, [open])

  if (!open) return null

  return (
    <div className={cn('fixed inset-0 z-50 flex', side === 'left' ? 'justify-start' : 'justify-end')}>
      <button
        type="button"
        aria-label="Закрыть"
        className="absolute inset-0 bg-scrim print:hidden"
        onClick={onClose}
      />
      <div
        ref={panel}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={cn(
          'relative flex h-full w-full min-w-0 max-w-xl flex-col overflow-y-auto',
          'border-hairline bg-surface-1 shadow-e3',
          side === 'left' ? 'max-w-sm border-r' : 'border-l',
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-hairline bg-surface-1 px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <h2
              ref={heading}
              id={headingId}
              tabIndex={-1}
              className="truncate text-title-2 font-semibold text-ink-1 focus:outline-none"
              title={typeof title === 'string' ? title : undefined}
            >
              {title}
            </h2>
            {subtitle && <p className="mt-1 text-caption text-ink-3 [overflow-wrap:anywhere]">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-sm px-2 py-1 text-body text-ink-2 hover:bg-surface-3 hover:text-ink-1 print:hidden [@media(pointer:coarse)]:min-h-11"
          >
            Закрыть
          </button>
        </div>
        <div className="min-w-0 flex-1 p-4 sm:p-6">{children}</div>
        {footer && (
          <div className="sticky bottom-0 z-10 border-t border-hairline bg-surface-1 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
